// src/components/Finanzas/subtabs/MesTab.jsx
// Finanzas → Mes: one calendar month (or the last 30 days) at a glance.
//
// Two grains, never mixed in one card:
//   issue date   → facturas, ingreso, pasantes, ticket promedio, operadores
//   payment date → cobrado and the payment-method donut
// An invoice issued in September and paid in October counts toward September's
// ingreso and October's cobrado. v_finanzas_mensual.cobrado_de_lo_facturado
// answers a different question and is deliberately not used for Cobrado.
//
// Every figure is a view column or a finanzas_rango / finanzas_buckets value —
// no money is added up here. Calendar mode reads the monthly views; "Últimos
// 30 días" reads finanzas_rango, because the views only bucket calendar months.

import React, { useMemo, useState } from 'react';
import {
  useMesFinanzas,
  useOperadoresMes,
  useAlertaClasificacion,
  useRangoFinanzas,
  useFacturasRango,
  useIngresoSerie,
} from '../hooks/useFinanzasData';
import { fmtUsd, fmtLb, tipoMeta, statusMeta, INGRESO_VS_PASANTES_NOTE } from '../utils/facturaMath';
import { svTodayStr, addDaysStr, calendarMonthRange, rollingRange, formatSvDate } from '../utils/timeBoundaries';
import RevenueBarChart from '../components/RevenueBarChart';
import MetodosDonut from '../components/MetodosDonut';

const ALERTA_TOOLTIP =
  'Impuestos capturados como ajuste manual. El ingreso propio está inflado por este monto y los pasantes desinflados por el mismo. Pendiente de reclasificar.';

const COBRADO_OPERADOR_NOTE =
  'Lo cobrado de las facturas que este operador emitió en el período — no lo que entró a caja en el período.';

const TIPO_FILTROS = ['todos', 'casillero', 'encomienda'];

const textOrDash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

// First day of the month `n` months before the month containing dateStr.
const monthsBack = (dateStr, n) => {
  let start = calendarMonthRange(dateStr).startStr;
  for (let i = 0; i < n; i += 1) start = calendarMonthRange(addDaysStr(start, -1)).startStr;
  return start;
};

const TipoChip = ({ tipo }) => {
  const meta = tipoMeta(tipo);
  return (
    <span className="fin-chip" style={{ background: `${meta.color}1f`, color: meta.color, borderColor: `${meta.color}66` }}>
      {meta.label}
    </span>
  );
};

const StatusChip = ({ status }) => {
  const meta = statusMeta(status);
  return <span className="fin-chip" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>;
};

const PasantesInfo = () => (
  <span className="fin-info" title={INGRESO_VS_PASANTES_NOTE} aria-label={INGRESO_VS_PASANTES_NOTE} role="img">i</span>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="fin-banner fin-banner--error" role="alert">
    <span>{message}</span>
    {onRetry && <button type="button" className="fin-btn" onClick={onRetry}>Reintentar</button>}
  </div>
);

// Renders only while the view has a positive amount for the period, so it
// disappears on its own once the adjustments are reclassified.
const AlertaChip = ({ alerta }) => {
  if (!alerta || !(Number(alerta.monto) > 0)) return null;
  return (
    <span className="fin-chip fin-chip--alerta" title={ALERTA_TOOLTIP} aria-label={`${fmtUsd(alerta.monto)} por reclasificar. ${ALERTA_TOOLTIP}`}>
      ⚠ {fmtUsd(alerta.monto)} por reclasificar
    </span>
  );
};

const MesTab = () => {
  const today = svTodayStr();
  const [modo, setModo] = useState('mes');            // 'mes' | 'rolling'
  const [mes, setMes] = useState(today.slice(0, 7));  // 'YYYY-MM'
  const [grano, setGrano] = useState('day');
  const [tipoFiltro, setTipoFiltro] = useState('todos');

  const esMes = modo === 'mes';
  const periodo = `${mes}-01`;
  const rango = useMemo(
    () => (esMes ? calendarMonthRange(periodo) : rollingRange(today, 30)),
    [esMes, periodo, today]
  );
  const serieDesde = grano === 'month' ? monthsBack(rango.endStr, 5) : rango.startStr;

  // Calendar mode reads the monthly views; rolling mode passes null so those hooks stay idle.
  const mesData   = useMesFinanzas(esMes ? periodo : null);
  const opsMes    = useOperadoresMes(esMes ? periodo : null);
  const alertaMes = useAlertaClasificacion(esMes ? periodo : null);
  const rangoData = useRangoFinanzas(rango.startStr, rango.endStr);
  const facturasRolling = useFacturasRango(esMes ? null : rango.startStr, esMes ? null : rango.endStr);
  const serie = useIngresoSerie(serieDesde, rango.endStr, grano);

  const all = [mesData, opsMes, alertaMes, rangoData, facturasRolling, serie];
  if (all.some((h) => h.viewsMissing)) {
    return <div className="fin-banner fin-banner--warn">Las vistas de Finanzas no están disponibles. Contacta al administrador.</div>;
  }

  const r = rangoData.data;
  const resumen    = esMes ? mesData.data?.mensual : r?.resumen;
  const cobranza   = esMes ? mesData.data?.cobranza : r?.cobranza;
  const operadores = (esMes ? opsMes.data : r?.operadores) || [];
  const alerta     = esMes ? alertaMes.data : r?.alerta;
  const facturas   = (esMes ? mesData.data?.facturas : facturasRolling.data) || [];
  // Payment-date total for either mode, summed server-side by finanzas_rango.
  const cobrado    = r?.cobrado;
  const top        = operadores[0] || null; // already ordered by ingreso desc

  const resumenLoading  = esMes ? mesData.loading : rangoData.loading;
  const resumenError    = esMes ? mesData.error : rangoData.error;
  const resumenRetry    = esMes ? mesData.refetch : rangoData.refetch;
  const facturasLoading = esMes ? mesData.loading : facturasRolling.loading;
  const facturasError   = esMes ? null : facturasRolling.error; // calendar-mode error already shown above
  const opsError        = esMes ? opsMes.error : null;
  const periodLabel     = rango.label;

  const facturasFiltradas = tipoFiltro === 'todos' ? facturas : facturas.filter((f) => f.tipo === tipoFiltro);

  // Counts come from the view, never from rows: facturas excludes anuladas by design.
  const filtroLabel = {
    todos:      `Todos · ${resumen?.facturas ?? 0} activas, ${resumen?.anuladas ?? 0} anuladas`,
    casillero:  `${tipoMeta('casillero').label} · ${resumen?.casillero ?? 0}`,
    encomienda: `${tipoMeta('encomienda').label} · ${resumen?.encomienda ?? 0}`,
  };

  return (
    <div>
      {/* ── Period selector ─────────────────────────────────────────────── */}
      <div className="fin-selector">
        <div className="fin-seg" role="group" aria-label="Tipo de período">
          <button type="button" className={`fin-seg-btn ${esMes ? 'is-active' : ''}`} aria-pressed={esMes} onClick={() => setModo('mes')}>
            Mes calendario
          </button>
          <button type="button" className={`fin-seg-btn ${!esMes ? 'is-active' : ''}`} aria-pressed={!esMes} onClick={() => setModo('rolling')}>
            Últimos 30 días
          </button>
        </div>
        {esMes && (
          <>
            <label htmlFor="fin-mes">Mes</label>
            <input
              id="fin-mes"
              type="month"
              className="fin-month"
              value={mes}
              max={today.slice(0, 7)}
              onChange={(e) => { if (e.target.value) setMes(e.target.value); }}
            />
          </>
        )}
        {!esMes && (
          <span className="fin-muted" style={{ padding: 0 }}>
            {formatSvDate(rango.startStr)} – {formatSvDate(rango.endStr)}
          </span>
        )}
      </div>

      {/* ── KPI cards ───────────────────────────────────────────────────── */}
      {resumenError && <ErrorBanner message={resumenError} onRetry={resumenRetry} />}
      {!resumenError && resumenLoading && !resumen && <div className="fin-muted">Cargando el período…</div>}
      {!resumenError && !resumenLoading && !resumen && (
        <div className="fin-muted">No hay facturas emitidas en {periodLabel}.</div>
      )}

      {resumen && (
        <div className="fin-cards">
          <div className="fin-card">
            <div className="fin-card-label">Facturas del período</div>
            <div className="fin-card-period">{periodLabel}</div>
            <div className="fin-card-value">{resumen.facturas ?? 0}</div>
            <div className="fin-card-sub">— {resumen.anuladas ?? 0} anuladas</div>
          </div>

          <div className="fin-card">
            <div className="fin-card-label">Ingreso del tenant</div>
            <div className="fin-card-period">{periodLabel} · por fecha de emisión</div>
            <div className="fin-card-value">{fmtUsd(resumen.ingreso)}</div>
            <div className="fin-card-sub">Flete + recargos + ajustes − descuentos</div>
            <AlertaChip alerta={alerta} />
            {/* Pasantes share this card's grain (issue date); the Cobrado card is payment date. */}
            <div className="fin-card-divider" title={INGRESO_VS_PASANTES_NOTE}>
              <div className="fin-card-label">Pasantes <PasantesInfo /></div>
              <div className="fin-card-value fin-card-value--sm">{fmtUsd(resumen.pasantes)}</div>
              <div className="fin-card-sub">DAI + IVA por cuenta de la DGA</div>
              <AlertaChip alerta={alerta} />
            </div>
          </div>

          <div className="fin-card">
            <div className="fin-card-label">Cobrado al cliente</div>
            <div className="fin-card-period">{periodLabel} · por fecha de pago</div>
            <div className="fin-card-value">{rangoData.error ? '—' : fmtUsd(cobrado)}</div>
            <div className="fin-card-sub">
              {rangoData.error ? rangoData.error : 'Pagos registrados en el período, sin importar cuándo se emitió la factura'}
            </div>
          </div>

          <div className="fin-card">
            <div className="fin-card-label">Ticket promedio</div>
            <div className="fin-card-period">{periodLabel}</div>
            <div className="fin-card-value">{resumen.ticket_promedio === null || resumen.ticket_promedio === undefined ? '—' : fmtUsd(resumen.ticket_promedio)}</div>
            <div className="fin-card-sub">Ingreso / facturas</div>
          </div>

          <div className="fin-card">
            <div className="fin-card-label">Top operador</div>
            <div className="fin-card-period">{periodLabel}</div>
            <div className="fin-card-value fin-card-value--text">{top ? top.operador : '—'}</div>
            <div className="fin-card-sub">
              {top ? `${top.facturas} facturas · ${fmtUsd(top.ingreso)}` : opsError || 'Sin facturas en el período'}
            </div>
          </div>
        </div>
      )}

      {/* ── Charts ──────────────────────────────────────────────────────── */}
      <div className="fin-charts">
        <RevenueBarChart
          rows={serie.data}
          desde={serieDesde}
          hasta={rango.endStr}
          grano={grano}
          onGranoChange={setGrano}
          periodLabel={periodLabel}
          loading={serie.loading}
          error={serie.error}
          onRetry={serie.refetch}
        />
        <MetodosDonut
          rows={cobranza}
          total={cobrado}
          periodLabel={periodLabel}
          loading={resumenLoading || rangoData.loading}
          error={rangoData.error}
          onRetry={rangoData.refetch}
        />
      </div>

      {/* ── Invoices ────────────────────────────────────────────────────── */}
      <section className="fin-section">
        <div className="fin-section-head">
          <h3 className="fin-section-title">Facturas emitidas · {periodLabel}</h3>
          <div className="fin-seg" role="group" aria-label="Filtrar por tipo">
            {TIPO_FILTROS.map((t) => (
              <button
                key={t}
                type="button"
                className={`fin-seg-btn ${tipoFiltro === t ? 'is-active' : ''}`}
                aria-pressed={tipoFiltro === t}
                onClick={() => setTipoFiltro(t)}
              >
                {filtroLabel[t]}
              </button>
            ))}
          </div>
        </div>

        {facturasError && <ErrorBanner message={facturasError} onRetry={facturasRolling.refetch} />}
        {!facturasError && facturasLoading && facturas.length === 0 && <div className="fin-muted">Cargando facturas…</div>}
        {!facturasError && !facturasLoading && facturasFiltradas.length === 0 && (
          <div className="fin-muted">No hay facturas de este tipo en el período.</div>
        )}
        {facturasFiltradas.length > 0 && (
          <div className="fin-table fin-table--scroll">
            <div className="fin-table-inner">
              <div className="fin-tr fin-tr--head fin-cols-mes">
                <div className="fin-td">Factura</div>
                <div className="fin-td">Cliente</div>
                <div className="fin-td">Tipo</div>
                <div className="fin-td">Operador</div>
                <div className="fin-td fin-td--num">Lb</div>
                <div className="fin-td fin-td--num">Flete</div>
                <div className="fin-td fin-td--num">Recargo</div>
                <div className="fin-td fin-td--num">Ajuste</div>
                <div className="fin-td fin-td--num">Descuento</div>
                <div className="fin-td fin-td--num">Ingreso</div>
                <div className="fin-td fin-td--num">Pasantes</div>
                <div className="fin-td fin-td--num">Total</div>
                <div className="fin-td fin-td--num">Cobrado</div>
                <div className="fin-td fin-td--num">Saldo</div>
                <div className="fin-td">Estado</div>
                <div className="fin-td">Fecha</div>
              </div>
              {facturasFiltradas.map((f) => (
                <div key={f.id} className={`fin-tr fin-cols-mes ${f.es_anulada ? 'fin-tr--void' : ''}`}>
                  <div className="fin-td fin-td--strike" data-label="Factura">{textOrDash(f.invoice_number)}</div>
                  <div className="fin-td" data-label="Cliente">{textOrDash(f.cliente)}</div>
                  <div className="fin-td" data-label="Tipo"><TipoChip tipo={f.tipo} /></div>
                  <div className="fin-td" data-label="Operador">{textOrDash(f.operador)}</div>
                  <div className="fin-td fin-td--num" data-label="Lb">{fmtLb(f.peso_lb)}</div>
                  <div className="fin-td fin-td--num" data-label="Flete">{fmtUsd(f.flete)}</div>
                  <div className="fin-td fin-td--num" data-label="Recargo">{fmtUsd(f.recargo)}</div>
                  <div className="fin-td fin-td--num" data-label="Ajuste">{fmtUsd(f.ajuste)}</div>
                  <div className="fin-td fin-td--num" data-label="Descuento">{fmtUsd(f.descuento)}</div>
                  <div className="fin-td fin-td--num" data-label="Ingreso">{fmtUsd(f.ingreso)}</div>
                  <div className="fin-td fin-td--num" data-label="Pasantes">{fmtUsd(f.pasantes)}</div>
                  <div className="fin-td fin-td--num fin-td--strike" data-label="Total">{fmtUsd(f.total)}</div>
                  <div className="fin-td fin-td--num" data-label="Cobrado">{fmtUsd(f.cobrado)}</div>
                  <div className="fin-td fin-td--num" data-label="Saldo">{fmtUsd(f.saldo)}</div>
                  <div className="fin-td" data-label="Estado"><StatusChip status={f.status} /></div>
                  <div className="fin-td" data-label="Fecha">{formatSvDate(f.fecha_emision)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Operators ───────────────────────────────────────────────────── */}
      <section className="fin-section">
        <h3 className="fin-section-title">Por operador · {periodLabel}</h3>
        {opsError && <ErrorBanner message={opsError} onRetry={opsMes.refetch} />}
        {!opsError && operadores.length === 0 && !(esMes ? opsMes.loading : rangoData.loading) && (
          <div className="fin-muted">Ningún operador emitió facturas en el período.</div>
        )}
        {operadores.length > 0 && (
          <div className="fin-table">
            <div className="fin-tr fin-tr--head fin-cols-operadores">
              <div className="fin-td">Operador</div>
              <div className="fin-td fin-td--num">Facturas</div>
              <div className="fin-td fin-td--num">Anuladas</div>
              <div className="fin-td fin-td--num">Ingreso</div>
              <div className="fin-td fin-td--num" title={COBRADO_OPERADOR_NOTE}>Cobrado de lo facturado</div>
              <div className="fin-td fin-td--num">Descuento</div>
              <div className="fin-td fin-td--num">Ticket promedio</div>
            </div>
            {operadores.map((o) => (
              <div key={o.operador_id || o.operador} className="fin-tr fin-cols-operadores">
                <div className="fin-td" data-label="Operador">
                  {textOrDash(o.operador)}
                  {o.operador_rol && <span className="fin-muted-inline"> · {o.operador_rol}</span>}
                </div>
                <div className="fin-td fin-td--num" data-label="Facturas">{o.facturas ?? 0}</div>
                <div className="fin-td fin-td--num" data-label="Anuladas">{o.anuladas ?? 0}</div>
                <div className="fin-td fin-td--num" data-label="Ingreso">{fmtUsd(o.ingreso)}</div>
                <div className="fin-td fin-td--num" data-label="Cobrado de lo facturado" title={COBRADO_OPERADOR_NOTE}>{fmtUsd(o.cobrado)}</div>
                <div className="fin-td fin-td--num" data-label="Descuento">{fmtUsd(o.descuento)}</div>
                <div className="fin-td fin-td--num" data-label="Ticket promedio">
                  {o.ticket_promedio === null || o.ticket_promedio === undefined ? '—' : fmtUsd(o.ticket_promedio)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MesTab;
